import type { Fetcher } from '@/lib/api/fetcher';

import type {
  Breach,
  Correction,
  Dsr,
  DsrPage,
  ExportUrl,
  Fulfil,
  MyErasure,
  Notice,
  Retention,
} from './schemas';
import {
  breachListSchema,
  breachSchema,
  correctionSchema,
  dsrPageSchema,
  dsrSchema,
  exportUrlSchema,
  fulfilSchema,
  myErasureOrNullSchema,
  myErasureSchema,
  noticeSchema,
  retentionSchema,
} from './schemas';

/**
 * One function per BP-03 §5 `/dpdp/*` route the §6 screens use, over the shared
 * transport (`lib/api/fetcher`): cookie session, `X-CSRF` on unsafe methods, RFC 9457
 * problems on failure.
 *
 * `GET /dpdp/notice` is **public** — a data principal has to be able to read it before
 * consenting to anything — so the client app calls it with no session at all.
 */

/** Query for the DSR queue (§6: "sorted by due_on, overdue red"). */
export interface DsrQuery {
  status?: string;
  clientId?: string;
  openOnly?: boolean;
  cursor?: string;
  limit?: number;
}

/** The facts §6's `BreachWizard` collects (Rule 7(1)). */
export interface BreachDraft {
  title: string;
  description: string;
  detected_at: string;
  data_categories: string[];
  affected_client_ids: string[];
  mitigation: string | null;
  client_action: string | null;
}

/** Every `/dpdp/*` call the §6 screens make. */
export interface DpdpApi {
  readNotice: () => Promise<Notice>;
  listDsr: (query?: DsrQuery) => Promise<DsrPage>;
  openDsr: (
    body: { client_id: string; type: string; received_via: string },
    idempotencyKey: string,
  ) => Promise<Dsr>;
  verifyDsr: (id: string, note: string | null) => Promise<Dsr>;
  fulfilDsr: (id: string, note: string | null) => Promise<Fulfil>;
  rejectDsr: (id: string, note: string | null) => Promise<Dsr>;
  cancelDsr: (id: string, note: string | null) => Promise<Dsr>;
  exportUrl: (id: string) => Promise<ExportUrl>;
  applyCorrection: (id: string, payload: Record<string, unknown>) => Promise<Correction>;
  listBreaches: () => Promise<Breach[]>;
  openBreach: (body: BreachDraft, idempotencyKey: string) => Promise<Breach>;
  notifyBreachClients: (id: string) => Promise<Breach>;
  markBoardReported: (id: string, reference: string | null) => Promise<Breach>;
  closeBreach: (id: string, postmortem: string | null) => Promise<Breach>;
  readRetention: () => Promise<Retention>;
  /** BP-11 R17: this client's own open erasure request, or `null`. */
  readMyErasure: () => Promise<MyErasure | null>;
  /** BP-11 R17: ask for this client's own record to be erased. */
  requestMyErasure: () => Promise<MyErasure>;
}

/** Build the `?a=b` part of the queue URL, leaving absent filters out entirely. */
export function dsrQueryString(query: DsrQuery = {}): string {
  const search = new URLSearchParams();
  if (query.status !== undefined) {
    search.set('status', query.status);
  }
  if (query.clientId !== undefined) {
    search.set('client_id', query.clientId);
  }
  if (query.openOnly === true) {
    search.set('open_only', 'true');
  }
  if (query.cursor !== undefined) {
    search.set('cursor', query.cursor);
  }
  if (query.limit !== undefined) {
    search.set('limit', String(query.limit));
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/** Bind the §5 `/dpdp/*` routes to a transport. */
export function createDpdpApi(fetcher: Fetcher): DpdpApi {
  const idem = (key: string): Record<string, string> => ({ 'Idempotency-Key': key });
  return {
    readNotice: () => fetcher.json('/v1/dpdp/notice', { method: 'GET' }, noticeSchema),
    readMyErasure: () => fetcher.json('/v1/me/erase', { method: 'GET' }, myErasureOrNullSchema),
    requestMyErasure: () =>
      fetcher.json('/v1/me/erase', { method: 'POST', body: {} }, myErasureSchema),
    listDsr: (query) =>
      fetcher.json(`/v1/dpdp/dsr${dsrQueryString(query)}`, { method: 'GET' }, dsrPageSchema),
    openDsr: (body, idempotencyKey) =>
      fetcher.json(
        '/v1/dpdp/dsr',
        { method: 'POST', body, headers: idem(idempotencyKey) },
        dsrSchema,
      ),
    verifyDsr: (id, note) =>
      fetcher.json(`/v1/dpdp/dsr/${id}/verify`, { method: 'POST', body: { note } }, dsrSchema),
    fulfilDsr: (id, note) =>
      fetcher.json(`/v1/dpdp/dsr/${id}/fulfil`, { method: 'POST', body: { note } }, fulfilSchema),
    rejectDsr: (id, note) =>
      fetcher.json(`/v1/dpdp/dsr/${id}/reject`, { method: 'POST', body: { note } }, dsrSchema),
    cancelDsr: (id, note) =>
      fetcher.json(`/v1/dpdp/dsr/${id}/cancel`, { method: 'POST', body: { note } }, dsrSchema),
    exportUrl: (id) =>
      fetcher.json(`/v1/dpdp/dsr/${id}/export-url`, { method: 'GET' }, exportUrlSchema),
    applyCorrection: (id, payload) =>
      fetcher.json(
        `/v1/dpdp/dsr/${id}/apply-correction`,
        { method: 'POST', body: { payload } },
        correctionSchema,
      ),
    listBreaches: () => fetcher.json('/v1/dpdp/breaches', { method: 'GET' }, breachListSchema),
    openBreach: (body, idempotencyKey) =>
      fetcher.json(
        '/v1/dpdp/breaches',
        { method: 'POST', body, headers: idem(idempotencyKey) },
        breachSchema,
      ),
    notifyBreachClients: (id) =>
      fetcher.json(
        `/v1/dpdp/breaches/${id}/notify-clients`,
        { method: 'POST', body: {} },
        breachSchema,
      ),
    markBoardReported: (id, reference) =>
      fetcher.json(
        `/v1/dpdp/breaches/${id}/board-reported`,
        { method: 'POST', body: { reference } },
        breachSchema,
      ),
    closeBreach: (id, postmortem) =>
      fetcher.json(
        `/v1/dpdp/breaches/${id}/close`,
        { method: 'POST', body: { postmortem } },
        breachSchema,
      ),
    readRetention: () => fetcher.json('/v1/dpdp/retention', { method: 'GET' }, retentionSchema),
  };
}
